import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import React, {useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {useDispatch} from 'react-redux';

import {useCategories} from '../../../api/hooks/useCategories.hook';
import {mapLookupToLanguageHook} from '../../../api/hooks/useLookupTableData.hook';
import {useMarketplacePage} from '../../../api/hooks/useMarketplace.hook';
import {LookupTableBaseModel} from '../../../api/models/lookup-table-base-model';
import { User } from '../../../api/models/user';
import {fetchAllOriginators, setFilter, useAppSelector} from '../../../redux';
import {FilterExpression} from '../../../utils/_enums/filter-expression.enum';
import {TypographyVariantsEnum} from '../../../utils/_enums/typeography-variants.enum';
import {FilterItem} from '../../../utils/_models/filter-item';
import {InstancesFilter} from '../../../utils/_models/instances-filter';
import {Button} from '../../_shared/button/button';
import {Select} from '../../_shared/select/select';
import {Typography} from '../../_shared/typography/typography';
import styles from './main-filter-form.module.scss';

interface MainFilterFormProps {
    openInDialog?: boolean;
    onCloseModal?: () => void;
}

const MainFilterForm = (props: MainFilterFormProps) => {
    const dispatch = useDispatch();
    const categories = useCategories();
    const data = useMarketplacePage();
    const originators = useAppSelector<User[]>(state => state.productSlice.originators);
    const filter = useAppSelector<FilterItem[] | undefined>(state => state.productSlice.detailedInstances.filter);
    const {
        control,
        getValues,
        setValue,
        reset,
        handleSubmit,
    } = useForm<InstancesFilter>({
        mode: 'onTouched'
    });

    useEffect(() => {
        dispatch(fetchAllOriginators());
        if (props.openInDialog && filter) {
            const categoryId = filter.find(item => item.param === 'categoryId');
            const originatorsIds = filter.find(item => item.param === 'originatorId');
            if (categoryId) {
                setValue('categoryId', categoryId.value);
            }
            if (originatorsIds) {
                setValue('originatorsIds', originatorsIds.value);
            }
        }
    }, []);

    const resetFilter = () => {
        if (filter) {
            const currentFilters = [
                ...filter.filter(item =>
                    (
                        item.param === 'status' ||
                        item.param === 'sort' ||
                        item.param === 'owner')
                )
            ]
            reset();
            dispatch(setFilter(currentFilters));
        }
    }

    const changeFilter = (triggeredByForm?: boolean) => {
        if (filter) {
            const currentFilters = [
                ...filter.filter(item =>
                    (
                        item.param === 'sortBy' ||
                        item.param === 'status' ||
                        item.param === 'size' ||
                        item.param === 'sort' ||
                        item.param === 'owner' ||
                        item.param === 'page')
                )
            ]
            if (getValues('originatorsIds') && getValues('originatorsIds').length > 0) {
                currentFilters.push({
                    value: getValues('originatorsIds') ? getValues('originatorsIds').toString() : ' ',
                    param: 'originatorId',
                    comparisonType: FilterExpression.in
                });
            }
            if (getValues('categoryId')) {
                currentFilters.push({
                    value: getValues('categoryId') ?? '',
                    param: 'categoryId',
                    comparisonType: FilterExpression.equals
                })
            }
            if (!props.openInDialog || triggeredByForm) {
                dispatch(setFilter(currentFilters));
                if (props.onCloseModal) {
                    props.onCloseModal();
                }
            }
        }
    }

    return (
        <form className={styles['main-filter-form']} onSubmit={handleSubmit(() => changeFilter(true))}>
            <Typography variant={TypographyVariantsEnum.HEADING3_BOLD}
                        className={styles['filter-headline']}
            >{data.filterHeadline.headlineContent}
                <Button onClick={resetFilter} className={styles['reset-filter']} icon={{
                    element: (
                        <FontAwesomeIcon
                            icon={['fas', 'undo']}
                        />
                    ),
                    align: 'right'
                }}>
                </Button>
            </Typography>
            <Select
                control={control}
                label={data.productType.fieldLabel}
                placeholder={
                    data.productType.fieldHint
                }
                isClearable={true}
                onChange={() => changeFilter()}
                name="categoryId"
                options={mapLookupToLanguageHook(categories.data?.data as LookupTableBaseModel[]).map(cat => {
                    return {
                        value: cat.id,
                        label: cat.text
                    }
                })}
            />
            <Select
                control={control}
                label={data.originator.fieldLabel}
                placeholder={data.originator.fieldHint}
                isMulti={true}
                onChange={() => changeFilter()}
                name={'originatorsIds'}
                options={originators.map(org => {
                    return {
                        value: org.id,
                        label: `${org.firstName} ${org.lastName}`
                    }
                })}
            />
            {
                props.openInDialog ?
                    <Button>{data.applyFilter.buttonText}</Button> : null
            }
        </form>
    )
}

export default MainFilterForm;
